<template>
    <div class="ds-calendar" :class="{
        'ds-calendar-large': props.large,
        'ds-calendar-copy-states': props.copyStateStyles,
    }">

        <!-- Режим выбора месяца (precision='month') -->
        <template v-if="precision === 'month'">
            <div class="ds-calendar-header">
                <div class="ds-calendar-nav-button"
                    @click="pickerYear--">
                    <icon width="9" height="13"><IconChevronLeft /></icon>
                </div>
                <span class="ds-calendar-title">{{ pickerYear }}</span>
                <div class="ds-calendar-nav-button"
                    @click="pickerYear++">
                    <icon width="9" height="13"><IconChevronRight /></icon>
                </div>
            </div>
            <div class="ds-calendar-month-grid">
                <div v-for="(name, i) in MONTH_NAMES" :key="i"
                    class="ds-calendar-month-item"
                    :class="{ 'ds-calendar-month-item-selected': isPickerSelected(i) }"
                    @click="selectMonthValue(i)">
                    {{ name }}
                </div>
            </div>
        </template>

        <!-- Полный календарь (precision='day', по умолчанию) -->
        <template v-else>
            <div class="ds-calendar-header">
                <div class="ds-calendar-nav-button"
                    @click="prevMonth">
                    <icon width="9" height="13"><IconChevronLeft /></icon>
                </div>
                <span class="ds-calendar-title ds-calendar-title-clickable"
                    @click="openMonthPicker">
                    {{ headerLabel }}
                </span>
                <div class="ds-calendar-nav-button"
                    @click="nextMonth">
                    <icon width="9" height="13"><IconChevronRight /></icon>
                </div>
            </div>

            <div class="ds-calendar-weekdays">
                <div v-for="d in WEEK_DAYS" :key="d"
                    class="ds-calendar-weekday">
                    {{ d }}
                </div>
            </div>

            <div class="ds-calendar-grid">
                <div v-for="(cell, i) in cells" :key="i"
                    class="ds-calendar-cell">
                    <SmallTooltip v-if="dayTooltip(cell)" :text="dayTooltip(cell) || ''">
                        <div
                            class="ds-calendar-day"
                            :class="dayClass(cell)"
                            :data-copy-state="copyState(cell)"
                            :aria-disabled="isDisabled(cell)"
                            @click="onDayClick(cell)">
                            {{ cell.day }}
                        </div>
                    </SmallTooltip>
                    <div
                        v-else
                        class="ds-calendar-day"
                        :class="dayClass(cell)"
                        :data-copy-state="copyState(cell)"
                        :aria-disabled="isDisabled(cell)"
                        @click="onDayClick(cell)">
                        {{ cell.day }}
                    </div>
                </div>
            </div>

            <!-- Оверлей выбора месяца/года -->
            <div v-if="showMonthPicker" class="ds-calendar-month-picker">
                <div class="ds-calendar-header">
                    <div class="ds-calendar-nav-button"
                        @click="pickerYear--">
                        <icon width="9" height="13"><IconChevronLeft /></icon>
                    </div>
                    <span class="ds-calendar-title">{{ pickerYear }}</span>
                    <div class="ds-calendar-nav-button"
                        @click="pickerYear++">
                        <icon width="9" height="13"><IconChevronRight /></icon>
                    </div>
                </div>
                <div class="ds-calendar-month-grid">
                    <div v-for="(name, i) in MONTH_NAMES" :key="i"
                        class="ds-calendar-month-item"
                        :class="{ 'ds-calendar-month-item-selected': isPickerSelected(i) }"
                        @click="selectMonth(i)">
                        {{ name }}
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import IconChevronLeft from './icons/IconChevronLeft.vue'
import IconChevronRight from './icons/IconChevronRight.vue'
import SmallTooltip from '@/components/SmallTooltip.vue'

type Mode = 'single' | 'range' | 'multi'
type Precision = 'day' | 'month'

interface Cell {
    date: Dayjs
    day: number
    currentMonth: boolean
}

const props = withDefaults(defineProps<{
    modelValue?: Dayjs | [Dayjs, Dayjs] | Dayjs[] | null
    mode?: Mode
    precision?: Precision
    disabledDates?: Dayjs[]
    sourceDates?: Dayjs[]
    occupiedDates?: Dayjs[]
    hrDeviationDates?: Dayjs[]
    hrDeviationLabels?: Record<string, string>
    minDate?: Dayjs
    initialDate?: Dayjs
    large?: boolean
    copyStateStyles?: boolean
}>(), {
    mode: 'single',
    precision: 'day',
})

const emit = defineEmits<{
    'update:modelValue': [value: Dayjs | [Dayjs, Dayjs] | Dayjs[]]
}>()

const WEEK_DAYS = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС']
const MONTH_NAMES = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']

const cursor = ref((props.initialDate ?? dayjs()).startOf('month'))
const showMonthPicker = ref(false)
const pickerYear = ref(
    props.precision === 'month'
        ? ((props.modelValue as Dayjs)?.year() ?? dayjs().year())
        : cursor.value.year()
)

const headerLabel = computed(() => cursor.value.format('MMMM YYYY'))

function openMonthPicker() {
    pickerYear.value = cursor.value.year()
    showMonthPicker.value = true
}

function isPickerSelected(month: number): boolean {
    if (props.precision === 'month') {
        const mv = props.modelValue as Dayjs | null | undefined
        return !!mv && mv.month() === month && mv.year() === pickerYear.value
    }
    return cursor.value.month() === month && cursor.value.year() === pickerYear.value
}

// Выбор месяца в оверлее (precision='day')
function selectMonth(month: number) {
    cursor.value = dayjs().year(pickerYear.value).month(month).startOf('month')
    showMonthPicker.value = false
}

// Выбор месяца как конечного значения (precision='month')
function selectMonthValue(month: number) {
    const value = dayjs().year(pickerYear.value).month(month).startOf('month')
    cursor.value = value
    emit('update:modelValue', value)
}

const cells = computed<Cell[]>(() => {
    const start = cursor.value.startOf('month')
    const startWeekday = (start.day() + 6) % 7
    const daysInMonth = start.daysInMonth()
    const result: Cell[] = []

    for (let i = 0; i < startWeekday; i++) {
        result.push({
            date: start.subtract(startWeekday - i, 'day'),
            day: start.subtract(startWeekday - i, 'day').date(),
            currentMonth: false,
        })
    }
    for (let d = 1; d <= daysInMonth; d++) {
        result.push({ date: start.date(d), day: d, currentMonth: true })
    }
    const remaining = (7 - (result.length % 7)) % 7
    for (let i = 1; i <= remaining; i++) {
        result.push({
            date: start.add(daysInMonth - 1 + i, 'day'),
            day: i,
            currentMonth: false,
        })
    }
    return result
})

function isToday(cell: Cell) {
    return cell.date.isSame(dayjs(), 'day')
}

function isDisabled(cell: Cell) {
    if (props.minDate && cell.date.isBefore(props.minDate, 'day')) return true
    return isHrDeviation(cell)
        || isSource(cell)
        || props.disabledDates?.some(d => d.isSame(cell.date, 'day'))
        || false
}

function isSource(cell: Cell) {
    return props.sourceDates?.some(d => d.isSame(cell.date, 'day')) ?? false
}

function isOccupied(cell: Cell) {
    return props.occupiedDates?.some(d => d.isSame(cell.date, 'day')) ?? false
}

function isHrDeviation(cell: Cell) {
    return props.hrDeviationDates?.some(d => d.isSame(cell.date, 'day')) ?? false
}

function isSelected(cell: Cell): boolean {
    if (!props.modelValue) return false
    if (props.mode === 'single') return (props.modelValue as Dayjs).isSame(cell.date, 'day')
    if (props.mode === 'multi') return (props.modelValue as Dayjs[]).some(d => d.isSame(cell.date, 'day'))
    if (props.mode === 'range') {
        const [start, end] = props.modelValue as [Dayjs, Dayjs]
        return cell.date.isSame(start, 'day') || (end && cell.date.isSame(end, 'day'))
    }
    return false
}

function isInRange(cell: Cell): boolean {
    if (props.mode !== 'range' || !props.modelValue) return false
    const [start, end] = props.modelValue as [Dayjs, Dayjs]
    if (!start || !end) return false
    return cell.date.isAfter(start, 'day') && cell.date.isBefore(end, 'day')
}

function isRangeStart(cell: Cell): boolean {
    if (props.mode !== 'range' || !props.modelValue) return false
    const [start] = props.modelValue as [Dayjs, Dayjs]
    return !!start && cell.date.isSame(start, 'day')
}

function isRangeEnd(cell: Cell): boolean {
    if (props.mode !== 'range' || !props.modelValue) return false
    const [, end] = props.modelValue as [Dayjs, Dayjs]
    return !!end && cell.date.isSame(end, 'day')
}

function dayClass(cell: Cell) {
    if (!cell.currentMonth) return 'ds-calendar-day-muted'
    if (isHrDeviation(cell)) return 'ds-calendar-day-occupied ds-calendar-day-hr-deviation'
    if (isSource(cell)) return 'ds-calendar-day-source'
    if (isDisabled(cell)) return 'ds-calendar-day-disabled'
    if (isRangeStart(cell)) return 'ds-calendar-day-selected ds-calendar-day-range-start'
    if (isRangeEnd(cell)) return 'ds-calendar-day-selected ds-calendar-day-range-end'
    if (isInRange(cell)) return 'ds-calendar-day-range-middle'
    if (isSelected(cell)) return isOccupied(cell)
        ? 'ds-calendar-day-selected ds-calendar-day-selected-occupied'
        : 'ds-calendar-day-selected'
    if (isOccupied(cell)) return 'ds-calendar-day-occupied'
    if (isToday(cell)) return 'ds-calendar-day-today'
    return 'ds-calendar-day-default'
}

function copyState(cell: Cell) {
    if (!cell.currentMonth) return undefined
    if (isHrDeviation(cell)) return 'occupied hr-deviation'
    if (isSource(cell)) return 'source'
    if (isDisabled(cell)) return undefined
    if (isSelected(cell)) return isOccupied(cell) ? 'selected selected-occupied' : 'selected'
    if (isOccupied(cell)) return 'occupied'
    return undefined
}

function dayTooltip(cell: Cell) {
    if (!cell.currentMonth) return undefined
    if (isHrDeviation(cell)) {
        const label = props.hrDeviationLabels?.[cell.date.format('YYYY-MM-DD')]
        return label || 'Кадровое отклонение'
    }
    if (isSource(cell)) return undefined
    if (isOccupied(cell)) return 'Есть смены'
    return undefined
}

function onDayClick(cell: Cell) {
    if (!cell.currentMonth || isDisabled(cell)) return
    if (props.mode === 'single') {
        emit('update:modelValue', cell.date)
    } else if (props.mode === 'multi') {
        const current = (props.modelValue as Dayjs[]) ?? []
        const idx = current.findIndex(d => d.isSame(cell.date, 'day'))
        emit('update:modelValue', idx > -1
            ? current.filter((_, i) => i !== idx)
            : [...current, cell.date])
    } else if (props.mode === 'range') {
        const [start, end] = (props.modelValue as [Dayjs, Dayjs]) ?? [null, null]
        if (!start || (start && end)) {
            emit('update:modelValue', [cell.date, null as unknown as Dayjs])
        } else {
            emit('update:modelValue', cell.date.isBefore(start, 'day')
                ? [cell.date, start]
                : [start, cell.date])
        }
    }
}

function prevMonth() { cursor.value = cursor.value.subtract(1, 'month') }
function nextMonth() { cursor.value = cursor.value.add(1, 'month') }
</script>

<style scoped>
.ds-calendar-large {
    --calendar-day-size: 27px;
    --calendar-cell-size: 37px;
    --calendar-weekday-size: 9px;
    --calendar-day-font-size: 11px;
    width: 100%;
}

.ds-calendar-large .ds-calendar-weekdays,
.ds-calendar-large .ds-calendar-grid {
    grid-template-columns: repeat(7, var(--calendar-cell-size));
    justify-content: center;
}

.ds-calendar-copy-states [data-copy-state~='source'] {
    border: 2px solid var(--color-brand-main);
    color: var(--color-brand-main);
    cursor: default;
}

.ds-calendar-copy-states [data-copy-state~='occupied'] {
    background-color: var(--color-bg-muted);
    color: var(--calendar-text-color);
}

.ds-calendar-copy-states [data-copy-state~='hr-deviation'] {
    cursor: default;
}

.ds-calendar-copy-states [data-copy-state~='selected'] {
    background-color: var(--color-accent-soft);
    color: var(--color-brand-main);
}

.ds-calendar-copy-states [data-copy-state~='selected-occupied'] {
    border: 2px solid var(--color-border-muted);
}
</style>
